import type { ReactNode } from 'react';
import { Download, FileText } from 'lucide-react';
import DOMPurify from 'dompurify';
import { fetchDocumentObject } from '../lib/portal';

/**
 * Override for form-engine-portable's built-in 'info'/'info_text' field
 * type, registered via the `fieldComponents` extension point (BOP PR #1488 /
 * POSS "Download Template"). The built-in renderer only knows a plain,
 * unauthenticated `window.open(field.downloadUrl)` — this component adds an
 * authenticated Blob-fetch path for application-bound template fields (mirrors
 * ReferenceDetailsStep's viewPdf()), falling back to the vanilla behavior for
 * any other info field so nothing else on the platform changes.
 *
 * Registering an override replaces the ENTIRE built-in rendering for this
 * field type (not just the button), so the markup below deliberately mirrors
 * form-engine-portable's own 'info'/'info_text' renderer (v2.40.7) closely
 * enough that neither applicants nor visual regression checks can tell the
 * difference for fields that don't use a template link. That includes the
 * engine's `presentation` contract (Asana 1217761042299548):
 *
 * - `presentation: 'document'` → undecorated document treatment: no card
 *   background/border/padding, no leading icon, semantic <h3> label, and the
 *   engine's structured-content classes (headings/lists styled by the
 *   engine's own stylesheet, imported globally in main.tsx — no portal CSS).
 * - omitted, 'alert', or any unknown runtime value → the legacy alert card,
 *   byte-for-byte the same markup as before.
 *
 * The value is consumed exactly as authored by BackOffice — no field-name,
 * ID, or step-key inference.
 */
interface InfoField {
  label?: string;
  content?: string;
  description?: string;
  defaultValue?: string;
  downloadUrl?: string;
  downloadLabel?: string;
  referenceTemplateKey?: string;
  /** Authored by BackOffice; engine contract: 'alert' | 'document'. Unknown
   *  runtime values must preserve the legacy alert card. */
  presentation?: string;
}

interface InfoFieldWithTemplateDownloadProps {
  field: InfoField;
}

/**
 * Mirrors the engine's plain-text formatting for description/defaultValue in
 * document mode: `**bold**` spans and hanging indents for `•`/`-` bullet
 * lines. (The engine applies this in both modes; the alert card below keeps
 * its long-standing plain rendering to leave existing behavior untouched.)
 */
function formatPlainLine(line: string, key: number) {
  const isBullet = line.trim().startsWith('•') || line.trim().startsWith('-');
  const nodes: ReactNode[] = [];
  let text = '';
  let i = 0;
  while (i < line.length) {
    if (line.substring(i, i + 2) === '**') {
      if (text) {
        nodes.push(<span key={`text-${i}`}>{text}</span>);
        text = '';
      }
      const close = line.indexOf('**', i + 2);
      if (close !== -1) {
        nodes.push(
          <strong key={`bold-${i}`} className="font-semibold">
            {line.substring(i + 2, close)}
          </strong>,
        );
        i = close + 2;
      } else {
        text += '**';
        i += 2;
      }
    } else {
      text += line[i];
      i++;
    }
  }
  if (text) nodes.push(<span key={`text-${i}`}>{text}</span>);
  return (
    <div
      key={key}
      className="leading-relaxed"
      style={isBullet ? { paddingLeft: '1.25rem', textIndent: '-1rem' } : {}}
    >
      {nodes.length > 0 ? nodes : line}
    </div>
  );
}

function formatPlainText(value: string | undefined) {
  if (!value || typeof value !== 'string') return [];
  return value.split('\n').map((line, i) => formatPlainLine(line, i));
}

export function InfoFieldWithTemplateDownload({ field }: InfoFieldWithTemplateDownloadProps) {
  const hasDownloadLink = Boolean(field.downloadUrl && field.downloadLabel);
  // Strict equality, mirroring the engine: omitted/'alert'/unknown → alert card.
  const isDocument = field.presentation === 'document';
  // BackOffice-authored HTML is expected to be trusted, but the portal still
  // sanitizes at the browser boundary so a broken upstream contract cannot
  // turn an info field into an XSS vector.
  const sanitizedContent = field.content ? DOMPurify.sanitize(field.content) : '';

  const handleDownload = async () => {
    if (field.referenceTemplateKey && field.downloadUrl) {
      const { url } = await fetchDocumentObject(field.downloadUrl);
      window.open(url, '_blank', 'noopener');
      return;
    }
    if (field.downloadUrl) {
      window.open(field.downloadUrl, '_blank');
    }
  };

  return (
    <div
      data-info-presentation={isDocument ? 'document' : 'alert'}
      className={isDocument ? 'mb-4' : 'mb-4 rounded-lg p-4'}
      style={
        isDocument
          ? undefined
          : {
              backgroundColor: hasDownloadLink ? '#EFF6FF' : '#F9FAFB',
              color: hasDownloadLink ? '#1E40AF' : '#374151',
              border: hasDownloadLink ? '1px solid #BFDBFE' : '1px solid #E5E7EB',
            }
      }
    >
      <div className={isDocument ? '' : 'flex items-start gap-3'}>
        {!isDocument && hasDownloadLink && (
          <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          {field.label &&
            (isDocument ? (
              <h3 className="text-lg md:text-xl font-semibold text-foreground mb-3 break-words">
                {field.label}
              </h3>
            ) : (
              <div
                className={`font-semibold mb-3 text-base ${hasDownloadLink ? 'text-blue-900' : ''}`}
              >
                {field.label}
              </div>
            ))}
          <div
            className={
              isDocument
                ? 'text-base text-foreground leading-relaxed space-y-3 break-words [&_h1]:text-2xl [&_h1]:font-semibold [&_h2]:text-xl [&_h2]:font-semibold [&_h3]:text-lg [&_h3]:font-semibold [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1'
                : `text-sm leading-relaxed ${hasDownloadLink ? 'text-blue-800' : ''}`
            }
          >
            {sanitizedContent ? (
              <div dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
            ) : isDocument ? (
              <div>{formatPlainText(field.description || field.defaultValue || '')}</div>
            ) : (
              <div>{field.description || field.defaultValue || ''}</div>
            )}
          </div>
          {hasDownloadLink && (
            <div className="mt-3">
              <button
                type="button"
                onClick={handleDownload}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
              >
                <Download className="w-4 h-4" />
                {field.downloadLabel}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
